import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { ButtonBase } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import CreateEditFolderModal from '~/components/folders/CreateEditFolderModal';
import Select from '~/components/ui/Select';
import TextInput from '~/components/ui/TextInput';
import useQrCodeFolders from '~/hooks/useQrCodeFolders';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { updateQrCodeThunk } from '~/store/reducers/qr-code';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { COLORS } from '~/styles/colors';

type NameAndOrganizationBlockOldFlowProps = {
  onChangeName: (name: string) => void;
};
/**
 * @deprecated
 * Use `NameAndOrganizationBlock` instead.
 */
export default function NameAndOrganizationBlockOldFlow({ onChangeName }: NameAndOrganizationBlockOldFlowProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { data: folders = [] } = useQrCodeFolders();

  const styles = useStyles({ isBorder: !!folders.length });

  const qrCodeData = useSelector(getQrCodeReducerData);

  const [value, setValue] = useState(qrCodeData.data?.name || '');
  const [isOpenCreateFolderModal, setIsOpenCreateFolderModal] = useState(false);
  const [folderId, setFolderId] = useState<number | string>(
    typeof qrCodeData.data?.folder?.id === 'number' ? qrCodeData.data?.folder?.id : '',
  );

  const updateName = useDebouncedCallback(() => {
    dispatch(updateQrCodeThunk({ name: value }));
  }, 300);

  const handleChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);

    updateName();
  };

  const updateFolderId = (folderId: number) => {
    setFolderId(folderId);
    dispatch(updateQrCodeThunk({ folder_id: folderId }));
  };

  return (
    <CreateQRContentBlockLayout type="nameAndOrganization" defaultExpanded={false}>
      <TextInput
        onChange={handleChangeName}
        fullWidth
        value={value}
        inputProps={{ maxLength: 100 }}
        helperText={value.length === 100 && t('other:maxLength', { maxLength: 100 })}
        onBlur={() => {
          // TODO remove
          onChangeName(value);
          dispatch(updateQrCodeThunk({ name: value }));
        }}
        label={t('basicTemplateLabels:nameAndOrganisation.name')}
        placeholder={t('basicTemplateLabels:nameAndOrganisation.namePlaceholder')}
      />
      <Select
        items={folders.map((folder) => ({ label: folder.name, value: folder.id }))}
        onChange={(event) => {
          if (typeof event.target.value === 'number') {
            updateFolderId(event.target.value);
          }
        }}
        displayEmpty
        fullWidth
        label={t('builder:nameAndFolder.folder')}
        value={folderId}
        labelSx={{ marginTop: '16px' }}
        placeholder={t('builder:nameAndFolder.noFolder')}
        AdditionalTopMenuItem={
          <ButtonBase sx={styles.createFolder} onClick={() => setIsOpenCreateFolderModal(true)}>
            + {t('builder:nameAndFolder.createFolder')}
          </ButtonBase>
        }
      />
      {isOpenCreateFolderModal && (
        <CreateEditFolderModal
          isOpen={isOpenCreateFolderModal}
          onClose={() => setIsOpenCreateFolderModal(false)}
          onSuccessCreate={updateFolderId}
          type="create"
        />
      )}
    </CreateQRContentBlockLayout>
  );
}

function useStyles({ isBorder }) {
  return {
    createFolder: css`
      font-weight: 500;
      color: ${COLORS.primary};
      font-size: 16px;
      width: 100%;
      padding: 10px 0;
      justify-content: flex-start;
      border-bottom: ${isBorder ? 1 : 0}px solid ${COLORS.grey200};
    `,
  };
}
